"use client";

import AddIcon from '@mui/icons-material/Add';
import { Box, Typography, Button, Skeleton, Fab, useMediaQuery, useTheme } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import ApiKeysList from '@/components/ApiKeys/ApiKeysList';
import ApiDocsLinkButton from '@/components/ApiKeys/ApiDocsLinkButton';
import CreateKeyModal from '@/components/ApiKeys/CreateKeyModal';
import EmptyState from '@/components/ApiKeys/EmptyState';
import StoreQuotaDisplay from '@/components/ApiKeys/StoreQuotaDisplay';
import ApiPlansSection from '@/components/ApiPlans/ApiPlansSection';
import { RootState } from '@/store/types';
import { colors, typography } from '@/theme/colors';

/**
 * Store-level usage data (shared across all API keys)
 */
interface StoreUsage {
  requests_used: number;
  requests_limit: number;
  resets_at: string;
  plan_name?: string;
  plan_slug?: string;
}
interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
}
export default function ApiKeysPage() {
  const t = useTranslations('apiKeys');
  const tPlans = useTranslations('apiPlans');
  const {
    enqueueSnackbar
  } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [storeUsage, setStoreUsage] = useState<StoreUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const storeId = authUser?.store?.id ?? 0;
  const currentApiPlanSlug = storeUsage?.plan_slug || authUser?.store?.api_plan?.slug || null;

  // Handle payment redirect from Stripe Checkout
  const handlePaymentRedirect = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      enqueueSnackbar(tPlans('upgradeSuccess'), {
        variant: 'success'
      });
      window.history.replaceState({}, '', '/developer/api-keys');
      // Refetch to show updated plan limits after successful upgrade
      return true;
    } else if (paymentStatus === 'cancelled') {
      enqueueSnackbar(tPlans('upgradeCancelled'), {
        variant: 'info'
      });
      window.history.replaceState({}, '', '/developer/api-keys');
    }
    return false;
  }, [enqueueSnackbar, tPlans]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to create new key
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCreateModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token && {
          Authorization: `Bearer ${token}`
        })
      };
      const res = await fetch('/api/api-keys', {
        headers
      });
      if (!res.ok) throw new Error('Failed to fetch keys');
      const data = await res.json();
      setKeys(data.keys);

      // Fetch store-level usage ONCE using any active key
      // (All keys share the same store quota)
      const activeKey = data.keys.find((k: ApiKey) => k.is_active);
      if (activeKey) {
        try {
          const usageRes = await fetch(`/api/api-keys/${activeKey.id}/usage`, {
            headers
          });
          if (usageRes.ok) {
            const usage = await usageRes.json();
            setStoreUsage(usage);
          }
        } catch {
          // If usage fetch fails, continue without usage data
        }
      }
    } catch {
      setError(t('errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Fetch keys on mount + handle payment redirect
  useEffect(() => {
    const wasPaymentSuccess = handlePaymentRedirect();
    fetchKeys();

    // If returning from successful payment, refetch after a short delay
    // to allow Stripe webhook to update the plan in the database
    if (wasPaymentSuccess) {
      const timeout = setTimeout(() => {
        fetchKeys();
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [fetchKeys, handlePaymentRedirect]);
  const handleKeyCreated = () => {
    setCreateModalOpen(false);
    fetchKeys(); // Refresh list
  };
  const handleKeyRevoked = (keyId: number) => {
    setKeys(prev => prev.map(k => k.id === keyId ? {
      ...k,
      is_active: false
    } : k));
  };
  return <Box sx={{
    width: {
      xs: '100%',
      md: 'calc(100% - 90px)'
    },
    marginTop: '100px',
    marginLeft: {
      md: '85px',
      xs: '0px'
    },
    marginRight: 'auto',
    padding: {
      xs: 2,
      md: 4
    },
    maxWidth: '1200px',
    mx: {
      md: 'auto'
    }
  }} data-sentry-element="Box" data-sentry-component="ApiKeysPage" data-sentry-source-file="page.tsx">
      {/* Page Header */}
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      mb: 4,
      flexWrap: 'wrap',
      gap: 2
    }} data-sentry-element="Box" data-sentry-source-file="page.tsx">
        <Box data-sentry-element="Box" data-sentry-source-file="page.tsx">
          <Typography sx={{
          ...typography.presets.pageTitle,
          color: colors.text.primary
        }} data-sentry-element="Typography" data-sentry-source-file="page.tsx">
            {t('title')}
          </Typography>
          <Typography sx={{
          ...typography.presets.bodySecondary,
          color: colors.text.tertiary,
          mt: 0.5
        }} data-sentry-element="Typography" data-sentry-source-file="page.tsx">
            {t('subtitle')}
          </Typography>
        </Box>

        <Box sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center'
      }} data-sentry-element="Box" data-sentry-source-file="page.tsx">
          {!isLoading && !error && keys.length > 0 && <ApiDocsLinkButton label="Docs" variant="text" sx={{
          textTransform: 'none',
          minWidth: '44px',
          minHeight: '44px',
          color: colors.primary.main,
          '&:hover': {
            bgcolor: 'transparent',
            textDecoration: 'underline'
          }
        }} />}

          {keys.length > 0 && !isLoading && !isMobile && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)} sx={{
          bgcolor: colors.primary.main,
          textTransform: 'none',
          minWidth: '44px',
          minHeight: '44px',
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: colors.primary.hover
          },
          '&:active': {
            transform: 'scale(0.97)'
          }
        }}>
              {t('createKey')}
            </Button>}
        </Box>
      </Box>

      {/* Content */}
      {isLoading ? <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      py: 4
    }}>
          {/* Skeleton cards while loading */}
          {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={180} sx={{
        borderRadius: '12px'
      }} />)}
        </Box> : error ? <Box sx={{
      textAlign: 'center',
      py: 8
    }}>
          <Typography color="error" sx={{
        mb: 2
      }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={fetchKeys} sx={{
        textTransform: 'none',
        minWidth: '44px',
        minHeight: '44px'
      }}>
            {t('tryAgain')}
          </Button>
        </Box> : keys.length === 0 ? <>
          <EmptyState onCreateClick={() => setCreateModalOpen(true)} />
          {storeId > 0 && <ApiPlansSection currentPlanSlug={currentApiPlanSlug} storeId={storeId} onUpgradeComplete={fetchKeys} />}
        </> : <>
          {/* Store-wide quota display */}
          {storeUsage && <StoreQuotaDisplay used={storeUsage.requests_used} limit={storeUsage.requests_limit} resetsAt={storeUsage.resets_at} planName={storeUsage.plan_name} />}

          {/* API Plans Upgrade Section */}
          {storeId > 0 && <ApiPlansSection currentPlanSlug={currentApiPlanSlug} storeId={storeId} onUpgradeComplete={fetchKeys} />}

          {/* API Keys list */}
          <ApiKeysList keys={keys} onRevoke={handleKeyRevoked} storeUsage={storeUsage} />
        </>}

      {/* Mobile FAB - Thumb Zone */}
      {isMobile && keys.length > 0 && !isLoading && <Fab color="primary" aria-label={t('createKey')} onClick={() => setCreateModalOpen(true)} sx={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      width: 56,
      height: 56,
      bgcolor: colors.primary.main,
      '&:hover': {
        bgcolor: colors.primary.hover
      },
      '&:active': {
        transform: 'scale(0.95)'
      },
      zIndex: 1000
    }}>
          <AddIcon />
        </Fab>}

      {/* Create Modal */}
      <CreateKeyModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={handleKeyCreated} data-sentry-element="CreateKeyModal" data-sentry-source-file="page.tsx" />
    </Box>;
}